import { string, z } from "zod";
import { useTranslation } from "react-i18next";
import { isMobilePhone } from "./PhoneNumberValidation";
import { numberChecker } from "../../profile/form/Validation/UserInformationValidation";
import { isPasswordEmpty } from "../../../utils/Functions";

const defaultPasswordRestValues = {
  password: "",
  confirmPassword: "",
};

const defaultUserChangePasswordValues = {
  oldPassword: "",
  password: "",
  confirmPassword: "",
};

function getSignUpResolver(t) {
  return z
    .object({
      username: z.string().min(1, { message: t("CantBeEmpty") }),
      firstName: z.string().min(1, { message: t("CantBeEmpty") }),
      lastName: z.string().min(1, { message: t("CantBeEmpty") }),
      phoneNumber: z
        .string()
        .min(1, { message: t("CantBeEmpty") })
        .refine((value) => !value || isMobilePhone(value), {
          message: t("InvalidPhoneNumber"),
        }),

      clinic: z.object({
        title: z.string().min(1, { message: t("CantBeEmpty") }),
        id: z.number().min(1, { message: t("CantBeEmpty") }),
      }),
      dob: z.coerce.date().nullable().optional(),
      email: z.string().email({ message: t("InvalidEmail") }),
      password: z.string().min(8, { message: t("PasswordMustHave") }),
      confirmPassword: z.string().min(8, { message: t("PasswordMustHave") }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("PasswordNotMatched"),
      path: ["confirmPassword"],
    });
}

export function getLegalValueParser(t) {
  return z
    .object({
      oib: z.string().superRefine((data, ctx) => {
        return numberChecker(t, ctx, data, 13);
      }),
      username: z.string().min(1, { message: t("CantBeEmpty") }),
      firstName: z.string().min(1, { message: t("CantBeEmpty") }),
      phoneNumber: z
        .string()
        .min(1, { message: t("CantBeEmpty") })
        .refine((value) => !value || isMobilePhone(value), {
          message: t("InvalidPhoneNumber"),
        }),

      clinic: z.object({
        title: z.string().min(1, { message: t("CantBeEmpty") }),
        id: z.number().min(1, { message: t("CantBeEmpty") }),
      }),
      personToContact: z.string().min(1, { message: t("CantBeEmpty") }),
      address: z.string().min(1, { message: t("CantBeEmpty") }),
      country: z.string().min(1, { message: t("CantBeEmpty") }),
      place: z.string().min(1, { message: t("CantBeEmpty") }),
      city: z.string().min(1, { message: t("CantBeEmpty") }),
      province: z.string().min(1, { message: t("CantBeEmpty") }),
      zipcode: z.string().superRefine((data, ctx) => {
        return numberChecker(t, ctx, data, 5);
      }),
      shelter: z.boolean().optional(),
      email: z.string().email({ message: t("InvalidEmail") }),
      password: z.string().min(8, { message: t("PasswordMustHave") }),
      confirmPassword: z.string().min(8, { message: t("PasswordMustHave") }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("PasswordNotMatched"),
      path: ["confirmPassword"],
    });
}

function usePasswordResetResolver() {
  const { t } = useTranslation();

  return z
    .object({
      password: string().min(8, { message: t("PasswordMustHave") }),
      confirmPassword: string().min(8, { message: t("PasswordMustHave") }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("PasswordNotMatched"),
      path: ["confirmPassword"],
    });
}

function useUserChangePasswordResolver() {
  const { t } = useTranslation();

  return z
    .object({
      oldPassword: z.string().refine((val) => {
        if (isPasswordEmpty()) return true;
        return val.length >= 8;
      }, {
        message: t("PasswordMustHave"),
      }),
      password: string().min(8, { message: t("PasswordMustHave") }),
      confirmPassword: string().min(8, { message: t("PasswordMustHave") }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("PasswordNotMatched"),
      path: ["confirmPassword"],
    });
}

const defaultValues = [
  "firstName",
  "phoneNumber",
  "clinic",
  "email",
  "oib",
  "password",
  "clinic",
  "username",
];

const userValues = ["dob", "lastName"];
const legalValues = ["personToContact", "shelter"];
const location = ["address", "zipcode", "province", "city", "place", "country"];

const signUpValueParser = (value) => {
  const parsedValues = getSharedObj(value);
  const user = getUserObj(value);
  return {
    ...parsedValues,
    ...user,
  };
};

export const legalSignUpParser = (array) => {
  const locationObj = getLocationObject(array);
  const parsedValues = getSharedObj(array);
  const legal = getLegalValues(array);
  return {
    ...parsedValues,
    ...legal,
    location: locationObj,
  };
};

function getObjectCore(array, toReduce) {
  const parsedValues = toReduce.reduce((acc, key) => {
    acc[key] = array[key];
    return acc;
  }, {});
  return parsedValues;
}

function getLocationObject(array) {
  return getObjectCore(array, location);
}

function getSharedObj(array) {
  return getObjectCore(array, defaultValues);
}

function getUserObj(array) {
  return getObjectCore(array, userValues);
}

function getLegalValues(array) {
  return getObjectCore(array, legalValues);
}

const logInDefaultValues = {
  login: "",
  password: "",
};

function useLogInResolver(t) {
  return z.object({
    login: string().min(1, { message: t("CantBeEmpty") }),
    password: string().min(4, { message: t("PasswordMustHave") }),
  });
}

function setUserEditErrors(setError, errorMessage, t) {
  if (errorMessage == "PhoneNumberInUser") {
    setError(
      "phoneNumber",
      { type: "focus", message: t("PhoneNumberTaken") },
      { shouldFocus: true }
    );
    return false;
  }
  if (errorMessage == "Username is already in use") {
    setError(
      "username",
      { type: "focus", message: t("UserNameTaken") },
      { shouldFocus: true }
    );
    return false;
  }
  return true;
}

export {
  setUserEditErrors,
  getSignUpResolver,
  signUpValueParser,
  logInDefaultValues,
  isMobilePhone,
  useLogInResolver,
  defaultPasswordRestValues,
  usePasswordResetResolver,
  defaultUserChangePasswordValues,
  useUserChangePasswordResolver,
};
